import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

// ── Enums ─────────────────────────────────────────────────────────────────

export type DeliveryPlanType =
  | "OrderDelivery"
  | "ExpoDelivery"
  | "TrayDispatch"
  | "TrayCollection"
  | "MixedDelivery";

export type DeliveryPlanStatus =
  | "Draft"
  | "Scheduled"
  | "Published"
  | "DriverAccepted"
  | "InProgress"
  | "Completed"
  | "PartiallyCompleted"
  | "Cancelled"
  | "ReconciliationRequired";

export type DeliveryStopStatus =
  | "Planned"
  | "InProgress"
  | "Delivered"
  | "NotDelivered"
  | "Cancelled";

export type DeliveryFeeSource = "RouteTemplate" | "RouteStop" | "ManualOverride";

// ── DTOs ──────────────────────────────────────────────────────────────────

export interface DeliveryPlanStopDto {
  id: string;
  deliveryPlanId: string;
  orderId?: string;
  orderNumber?: string;
  customerId?: string;
  customerName?: string;
  routePointId?: string;
  routePointName?: string;
  collectionPointId?: string;
  sequenceNumber: number;
  plannedArrivalTime?: string;
  expectedDeliveryTime?: string;
  status: DeliveryStopStatus;
  notes?: string;
  resolvedDeliveryFee?: number;
  resolvedDeliveryFeeSource?: DeliveryFeeSource;
  items: DeliveryPlanStopItemDto[];
}

export interface DeliveryPlanStopItemDto {
  id: string;
  deliveryPlanStopId: string;
  orderId: string;
  orderItemId: string;
  fulfilmentReleaseItemId?: string;
  orderNumber: string;
  customerName: string;
  deliveryLocation: string;
  deliveryLatitude?: number | null;
  deliveryLongitude?: number | null;
  deliveryInstructions?: string | null;
  productName: string;
  variety?: string;
  trayType?: string;
  plannedQuantity: number;
  shortLoadedQuantity: number;
  executableQuantity: number;
  shortLoadReason?: string;
  shortLoadNotes?: string;
  shortLoadRecordedAt?: string;
  rowVersion: number;
  unit: string;
  unitName: string;
  previousDeliveryAttemptDispatchRecordId?: string;
}

export interface DeliveryPlanDto {
  id: string;
  name: string;
  planType: DeliveryPlanType;
  startDate: string;
  endDate: string;
  routeId?: string;
  routeName: string;
  driverId?: string;
  driverName: string;
  plannedDriverMobileLinked: boolean;
  plannedDriverUserActive: boolean;
  plannedDriverMobileReadiness: "Ready" | "Missing" | "Inactive";
  vehicleId?: string;
  vehicleRegistrationNumber: string;
  plannedDepartureTime?: string;
  /** @deprecated Derived trip-window end. Expected delivery is stop-level. */
  expectedDeliveryTime?: string;
  actualDepartureTime?: string;
  actualCompletionTime?: string;
  deliveryFee: number;
  deliveryFeeSource: DeliveryFeeSource;
  previousDeliveryFee?: number;
  deliveryFeeOverrideReason?: string;
  status: DeliveryPlanStatus;
  notes?: string;
  isActive: boolean;
  publishedAt?: string;
  publishedByUserId?: string;
  createdAt: string;
  updatedAt?: string;
  rowVersion: number;
  stops: DeliveryPlanStopDto[];
}

export interface DeliveryPlanSummaryDto {
  id: string;
  name: string;
  planType: DeliveryPlanType;
  startDate: string;
  endDate: string;
  routeName: string;
  driverName: string;
  plannedDriverMobileLinked: boolean;
  plannedDriverUserActive: boolean;
  plannedDriverMobileReadiness: "Ready" | "Missing" | "Inactive";
  vehicleRegistrationNumber: string;
  plannedDepartureTime?: string;
  /** @deprecated Derived trip-window end. Expected delivery is stop-level. */
  expectedDeliveryTime?: string;
  deliveryFee: number;
  status: DeliveryPlanStatus;
  stopCount: number;
  hasConflicts: boolean;
}

export interface DeliveryPlanListResultDto {
  items: DeliveryPlanSummaryDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface DeliveryPlanListQuery {
  search?: string;
  planType?: DeliveryPlanType;
  status?: DeliveryPlanStatus;
  startDateFrom?: string;
  startDateTo?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface DeliveryPlanStopInput {
  orderId?: string;
  customerId?: string;
  routePointId?: string;
  collectionPointId?: string;
  sequenceNumber: number;
  plannedArrivalTime?: string;
  expectedDeliveryTime?: string;
  notes?: string;
  items?: DeliveryPlanStopItemInput[];
}

export interface DeliveryPlanStopItemInput {
  orderId?: string;
  orderItemId: string;
  plannedQuantity: number;
  fulfilmentReleaseItemId?: string;
}

export interface CreateDeliveryPlanRequest {
  name: string;
  planType: DeliveryPlanType;
  startDate: string;
  endDate: string;
  routeId?: string;
  driverId?: string;
  vehicleId?: string;
  plannedDepartureTime?: string;
  expectedDeliveryTime?: string;
  deliveryFee?: number;
  deliveryFeeOverrideReason?: string;
  driverOverrideReason?: string;
  vehicleOverrideReason?: string;
  notes?: string;
  stops: DeliveryPlanStopInput[];
}

export interface UpdateDeliveryPlanRequest {
  name: string;
  planType: DeliveryPlanType;
  startDate: string;
  endDate: string;
  routeId?: string;
  driverId?: string;
  vehicleId?: string;
  plannedDepartureTime?: string;
  expectedDeliveryTime?: string;
  deliveryFee?: number;
  deliveryFeeOverrideReason?: string;
  driverOverrideReason?: string;
  vehicleOverrideReason?: string;
  status: DeliveryPlanStatus;
  isActive: boolean;
  notes?: string;
  stops: DeliveryPlanStopInput[];
}

export interface PublishDeliveryPlanRequest {
  notes?: string;
}

export interface CancelDeliveryPlanRequest {
  reason: string;
  rowVersion: number;
}

export interface ReassignDeliveryPlanRequest {
  driverId: string;
  vehicleId: string;
  plannedDepartureTime: string;
  expectedDeliveryTime: string;
  reason: string;
  driverOverrideReason?: string;
  vehicleOverrideReason?: string;
  notes?: string;
}

export interface CheckDeliveryPlanConflictsRequest {
  deliveryPlanId?: string;
  startDate: string;
  endDate: string;
  routeId?: string;
  driverId: string;
  vehicleId: string;
  plannedDepartureTime: string;
  expectedDeliveryTime: string;
  stops: DeliveryPlanStopInput[];
  driverOverrideReason?: string;
  vehicleOverrideReason?: string;
}

export interface DeliveryPlanConflictDto {
  code: string;
  message: string;
  field?: string;
}

export interface DeliveryPlanConflictCheckResultDto {
  hasConflicts: boolean;
  conflicts: DeliveryPlanConflictDto[];
  resolvedDeliveryFee: number;
  resolvedDeliveryFeeSource: DeliveryFeeSource;
}

export interface DeliveryPlanReassignResultDto {
  plan: DeliveryPlanDto;
  driverChanged: boolean;
  vehicleChanged: boolean;
}

export interface PlannerEligibleOrderSummaryDto {
  orderId: string;
  orderNumber: string;
  customerId: string;
  customerName: string;
  routeId?: string;
  routeName?: string;
  routeStopId?: string;
  routeStopName?: string;
  transportRateId?: string;
  pricingRegion?: string;
  deliveryAreaName?: string;
  requiresRouteStop?: boolean;
  deliveryLocation: string;
  deliveryLatitude?: number | null;
  deliveryLongitude?: number | null;
  expectedDeliveryDate?: string;
  orderedQuantity: number;
  deliveredQuantity: number;
  alreadyPlannedQuantity: number;
  availableToPlan: number;
  remainingAfterCurrentPlan: number;
  quantityPending: number;
  totalAmount: number;
  lines: PlannerEligibleOrderLineDto[];
}

export interface PlannerEligibleOrderLineDto {
  fulfilmentReleaseItemId: string;
  fulfilmentReleaseId: string;
  orderItemId: string;
  productName: string;
  variety?: string;
  trayType?: string;
  orderedQuantity: number;
  productionReadyQuantity: number;
  salesReleasedQuantity: number;
  deliveredQuantity: number;
  alreadyPlannedQuantity: number;
  availableToPlan: number;
  quantityPending: number;
  unit: string;
  unitName: string;
}

export interface PlannerEligibleOrderListQuery {
  search?: string;
  deliveryPlanId?: string;
  routeId?: string;
  location?: string;
  startDate?: string;
  endDate?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface PlannerEligibleOrderListResultDto {
  items: PlannerEligibleOrderSummaryDto[];
  locationCounts: Array<{ location: string; orderCount: number }>;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface RouteSuggestionResult {
  orderIds: string[];
  source: "Road" | "StraightLine" | "Manual";
  missingCoordinates: string[];
}

export interface DispatchExecutionQueueLineDto {
  deliveryPlanId: string;
  planName: string;
  planStatus: string;
  deliveryPlanStopItemId: string;
  rowVersion: number;
  orderId: string;
  orderNumber: string;
  customerName: string;
  orderItemId: string;
  productName: string;
  crop?: string;
  variety?: string;
  trayType?: string;
  plannedQuantity: number;
  shortLoadedQuantity: number;
  quantityToLoad: number;
  driverName: string;
  vehicleRegistrationNumber: string;
  routeName: string;
  plannedDepartureTime: string;
  deliveryLocation: string;
  dispatchState: string;
  deliveryOutcome: string;
  reconciliationState: string;
}

export interface DispatchExecutionQueueResultDto {
  items: DispatchExecutionQueueLineDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

// ── API Client ────────────────────────────────────────────────────────────

export const deliveryPlansApi = {
  suggestRoute: async (
    stops: Array<{ orderId: string; latitude?: number | null; longitude?: number | null }>,
    departure?: { latitude: number; longitude: number },
  ): Promise<RouteSuggestionResult> => {
    const res = await apiClient.post<RouteSuggestionResult>("/delivery-plans/suggest-route", {
      stops,
      departureLatitude: departure?.latitude,
      departureLongitude: departure?.longitude,
    });
    return res.data;
  },
  getExecutionQueue: async (search: string, pageNumber: number, pageSize = 25): Promise<DispatchExecutionQueueResultDto> => {
    const params = new URLSearchParams({ pageNumber: String(pageNumber), pageSize: String(pageSize) });
    if (search) params.set("search", search);
    const res = await apiClient.get<ApiResponse<DispatchExecutionQueueResultDto>>(`/delivery-plans/execution-queue?${params}`);
    return res.data.data!;
  },

  recordShortLoad: async (planId: string, data: { deliveryPlanStopItemId: string; actualLoadedQuantity: number; reason: string; notes?: string; rowVersion: number }): Promise<DeliveryPlanDto> => {
    const res = await apiClient.post<ApiResponse<DeliveryPlanDto>>(`/delivery-plans/${planId}/short-load`, data);
    return res.data.data!;
  },
  getPaged: async (query: DeliveryPlanListQuery): Promise<DeliveryPlanListResultDto> => {
    const params = new URLSearchParams();
    if (query.search) params.set("search", query.search);
    if (query.planType) params.set("planType", query.planType);
    if (query.status) params.set("status", query.status);
    if (query.startDateFrom) params.set("startDateFrom", query.startDateFrom);
    if (query.startDateTo) params.set("startDateTo", query.startDateTo);
    if (query.pageNumber) params.set("pageNumber", String(query.pageNumber));
    if (query.pageSize) params.set("pageSize", String(query.pageSize));
    const res = await apiClient.get<ApiResponse<DeliveryPlanListResultDto>>(
      `/delivery-plans/paged?${params.toString()}`
    );
    return res.data.data!;
  },

  getById: async (id: string): Promise<DeliveryPlanDto> => {
    const res = await apiClient.get<ApiResponse<DeliveryPlanDto>>(`/delivery-plans/${id}`);
    return res.data.data!;
  },

  getEligibleOrders: async (query: PlannerEligibleOrderListQuery): Promise<PlannerEligibleOrderListResultDto> => {
    const params = new URLSearchParams();
    if (query.search) params.set("search", query.search);
    if (query.deliveryPlanId) params.set("deliveryPlanId", query.deliveryPlanId);
    if (query.routeId) params.set("routeId", query.routeId);
    if (query.location) params.set("location", query.location);
    if (query.startDate) params.set("startDate", query.startDate);
    if (query.endDate) params.set("endDate", query.endDate);
    if (query.pageNumber) params.set("pageNumber", String(query.pageNumber));
    if (query.pageSize) params.set("pageSize", String(query.pageSize));
    const res = await apiClient.get<ApiResponse<PlannerEligibleOrderListResultDto>>(
      `/delivery-plans/eligible-orders?${params.toString()}`
    );
    return res.data.data!;
  },

  create: async (data: CreateDeliveryPlanRequest): Promise<DeliveryPlanDto> => {
    const res = await apiClient.post<ApiResponse<DeliveryPlanDto>>("/delivery-plans", data);
    return res.data.data!;
  },

  update: async (id: string, data: UpdateDeliveryPlanRequest): Promise<DeliveryPlanDto> => {
    const res = await apiClient.put<ApiResponse<DeliveryPlanDto>>(`/delivery-plans/${id}`, data);
    return res.data.data!;
  },

  checkConflicts: async (data: CheckDeliveryPlanConflictsRequest): Promise<DeliveryPlanConflictCheckResultDto> => {
    const res = await apiClient.post<ApiResponse<DeliveryPlanConflictCheckResultDto>>(
      "/delivery-plans/check-conflicts",
      data
    );
    return res.data.data!;
  },

  publish: async (id: string, data: PublishDeliveryPlanRequest): Promise<DeliveryPlanDto> => {
    const res = await apiClient.post<ApiResponse<DeliveryPlanDto>>(`/delivery-plans/${id}/publish`, data);
    return res.data.data!;
  },

  cancel: async (id: string, data: CancelDeliveryPlanRequest): Promise<DeliveryPlanDto> => {
    const res = await apiClient.post<ApiResponse<DeliveryPlanDto>>(`/delivery-plans/${id}/cancel`, data);
    return res.data.data!;
  },

  reassign: async (id: string, data: ReassignDeliveryPlanRequest): Promise<DeliveryPlanReassignResultDto> => {
    const res = await apiClient.post<ApiResponse<DeliveryPlanReassignResultDto>>(
      `/delivery-plans/${id}/reassign`,
      data
    );
    return res.data.data!;
  },
};
